import { loadSecret, saveSecret } from './session-store.ts';

export type AuthSession = {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Unix seconds. */
  readonly expiresAt: number;
  readonly user: { readonly id: string; readonly email: string };
};

export type AuthState = { status: 'loading' } | { status: 'signedOut'; message?: string } | { status: 'signedIn'; session: AuthSession };

/** What the app needs from authentication — Supabase Auth in production, dev-auth locally. */
export interface AuthClient {
  getState(): AuthState;
  subscribe(listener: (state: AuthState) => void): () => void;
  restore(): Promise<void>;
  signIn(email: string, password: string): Promise<{ ok: true } | { ok: false; reason: 'INVALID' | 'NETWORK' }>;
  signOut(): Promise<void>;
  /** A valid access token, refreshed when close to expiry; null when signed out. */
  accessToken(): Promise<string | null>;
  /**
   * A new password for the signed-in account, once the current one is right. Other devices are
   * signed out (they sign in with the new password); this one goes on.
   */
  changePassword(current: string, next: string): Promise<{ ok: true } | { ok: false; reason: 'WRONG_PASSWORD' | 'WEAK' | 'SAME' | 'NETWORK' }>;
}

/** How one sign-in service is spoken to: the development backend's or Supabase Auth's. */
type AuthBackend = {
  /** The session, or null for a wrong e-mail or password; throws when the service cannot be reached. */
  signIn(email: string, password: string): Promise<AuthSession | null>;
  /** The next session; 'expired' when the service no longer accepts the refresh token; null on trouble (try later). */
  refresh(refreshToken: string): Promise<AuthSession | 'expired' | null>;
  signOut(session: AuthSession): Promise<void>;
  /** Ends this one session only (the account's others go on). */
  endSession(session: AuthSession): Promise<void>;
  /** Sets the password with this session's rights; throws when the service cannot be reached. */
  changePassword(session: AuthSession, password: string): Promise<'OK' | 'WEAK' | 'SAME'>;
};

/** Where the session is kept (encrypted, session-store.ts); tests keep it in memory. */
export type SessionStore = { load(key: string): Promise<string | null>; save(key: string, value: string | null): Promise<void> };

const SESSION_KEY = 'session';

function createSessionAuthClient(backend: AuthBackend, store: SessionStore = { load: loadSecret, save: saveSecret }): AuthClient {
  let state: AuthState = { status: 'loading' };
  const listeners = new Set<(state: AuthState) => void>();
  let refreshing: Promise<AuthSession | null> | null = null;

  const setState = (next: AuthState) => {
    state = next;
    for (const listener of listeners) listener(state);
  };

  async function persist(session: AuthSession | null) {
    await store.save(SESSION_KEY, session ? JSON.stringify(session) : null);
  }

  async function refresh(current: AuthSession): Promise<AuthSession | null> {
    refreshing ??= (async () => {
      let session = current;
      try {
        // Another tab may have rotated the tokens already: use its newer session.
        const stored = await store.load(SESSION_KEY);
        const latest = stored ? (JSON.parse(stored) as AuthSession) : null;
        if (latest && latest.refreshToken !== session.refreshToken) {
          session = latest;
          if (latest.expiresAt - Date.now() / 1000 > 60) {
            setState({ status: 'signedIn', session: latest });
            return latest;
          }
        }
        const next = await backend.refresh(session.refreshToken);
        if (next === 'expired') {
          await persist(null);
          setState({ status: 'signedOut', message: 'sessionExpired' });
          return null;
        }
        if (next === null) return session; // server trouble: keep the session, try later
        await persist(next);
        setState({ status: 'signedIn', session: next });
        return next;
      } catch {
        return session; // offline: keep working locally
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async restore() {
      const stored = await store.load(SESSION_KEY);
      if (!stored) return setState({ status: 'signedOut' });
      const session = JSON.parse(stored) as AuthSession;
      setState({ status: 'signedIn', session });
      if (session.expiresAt - Date.now() / 1000 < 120) await refresh(session);
    },
    async signIn(email, password) {
      let session: AuthSession | null;
      try {
        session = await backend.signIn(email, password);
      } catch {
        return { ok: false, reason: 'NETWORK' };
      }
      if (!session) return { ok: false, reason: 'INVALID' };
      await persist(session);
      setState({ status: 'signedIn', session });
      return { ok: true };
    },
    async signOut() {
      if (state.status === 'signedIn') void backend.signOut(state.session).catch(() => undefined);
      await persist(null);
      setState({ status: 'signedOut' });
    },
    async accessToken() {
      if (state.status !== 'signedIn') return null;
      let session = state.session;
      if (session.expiresAt - Date.now() / 1000 < 60) session = (await refresh(session)) ?? session;
      return state.status === 'signedIn' ? session.accessToken : null;
    },
    async changePassword(current, next) {
      if (state.status !== 'signedIn') return { ok: false, reason: 'NETWORK' };
      const previous = state.session;
      // Signing in again proves the current password and gives a fresh session, which Supabase
      // wants for a password change; the change signs out the account's other sessions.
      let fresh: AuthSession | null;
      try {
        fresh = await backend.signIn(previous.user.email, current);
      } catch {
        return { ok: false, reason: 'NETWORK' };
      }
      if (!fresh) return { ok: false, reason: 'WRONG_PASSWORD' };
      let result: 'OK' | 'WEAK' | 'SAME' | null;
      try {
        result = await backend.changePassword(fresh, next);
      } catch {
        result = null;
      }
      if (result !== 'OK') {
        void backend.endSession(fresh).catch(() => undefined);
        return { ok: false, reason: result ?? 'NETWORK' };
      }
      // The session that set the password goes on; the one before is ended.
      await persist(fresh);
      setState({ status: 'signedIn', session: fresh });
      void backend.endSession(previous).catch(() => undefined);
      return { ok: true };
    },
  };
}

const post = (fetchImpl: typeof fetch, url: string, body: unknown, headers: Record<string, string> = {}) =>
  fetchImpl(url, { method: 'POST', headers: { 'content-type': 'application/json', ...headers }, body: JSON.stringify(body) });

/** tools/dev-backend's development login (the same session contract as Supabase Auth). */
export function createDevAuthClient(baseUrl: string, fetchImpl: typeof fetch = (input, init) => fetch(input, init)): AuthClient {
  return createSessionAuthClient({
    async signIn(email, password) {
      const response = await post(fetchImpl, `${baseUrl}/dev-auth/v1/login`, { email, password });
      return response.ok ? ((await response.json()) as AuthSession) : null;
    },
    async refresh(refreshToken) {
      const response = await post(fetchImpl, `${baseUrl}/dev-auth/v1/refresh`, { refreshToken });
      if (response.status === 401) return 'expired';
      return response.ok ? ((await response.json()) as AuthSession) : null;
    },
    async signOut(session) {
      await post(fetchImpl, `${baseUrl}/dev-auth/v1/logout`, { refreshToken: session.refreshToken });
    },
    async endSession(session) {
      await post(fetchImpl, `${baseUrl}/dev-auth/v1/logout`, { refreshToken: session.refreshToken });
    },
    async changePassword(session, password) {
      const response = await post(fetchImpl, `${baseUrl}/dev-auth/v1/password`, { password }, { authorization: `Bearer ${session.accessToken}` });
      if (response.ok) return 'OK';
      const code = response.status === 422 ? ((await response.json()) as { error?: { code?: string } }).error?.code : undefined;
      if (code === 'SAME_PASSWORD') return 'SAME';
      if (code === 'WEAK_PASSWORD') return 'WEAK';
      throw new Error(`dev-auth: HTTP ${response.status}`);
    },
  });
}

type SupabaseSession = { access_token: string; refresh_token: string; expires_at?: number; expires_in: number; user: { id: string; email?: string | null } };

/**
 * Supabase Auth over its REST API (e-mail and password; the project allows no sign-ups): the same
 * session as the development login, so the rest of the app does not know which one it talks to.
 */
export function createSupabaseAuthClient(
  projectUrl: string,
  publishableKey: string,
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
  store?: SessionStore,
): AuthClient {
  const base = `${projectUrl.replace(/\/+$/, '')}/auth/v1`;
  const headers = { apikey: publishableKey };
  const toSession = (body: SupabaseSession): AuthSession => ({
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: body.expires_at ?? Math.floor(Date.now() / 1000) + body.expires_in,
    user: { id: body.user.id, email: body.user.email ?? '' },
  });
  return createSessionAuthClient(
    {
      async signIn(email, password) {
        const response = await post(fetchImpl, `${base}/token?grant_type=password`, { email, password }, headers);
        // 400: wrong e-mail or password; anything else unexpected counts as "cannot reach".
        if (response.status === 400 || response.status === 401 || response.status === 422) return null;
        if (!response.ok) throw new Error(`Supabase Auth: HTTP ${response.status}`);
        return toSession((await response.json()) as SupabaseSession);
      },
      async refresh(refreshToken) {
        const response = await post(fetchImpl, `${base}/token?grant_type=refresh_token`, { refresh_token: refreshToken }, headers);
        if (response.status === 400 || response.status === 401) return 'expired';
        return response.ok ? toSession((await response.json()) as SupabaseSession) : null;
      },
      async signOut(session) {
        await post(fetchImpl, `${base}/logout`, {}, { ...headers, authorization: `Bearer ${session.accessToken}` });
      },
      async endSession(session) {
        await post(fetchImpl, `${base}/logout?scope=local`, {}, { ...headers, authorization: `Bearer ${session.accessToken}` });
      },
      async changePassword(session, password) {
        const response = await fetchImpl(`${base}/user`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json', ...headers, authorization: `Bearer ${session.accessToken}` },
          body: JSON.stringify({ password }),
        });
        if (response.ok) return 'OK';
        // Supabase: 422 same_password / weak_password (too short, or too easy for the project's rules).
        const code = response.status === 422 || response.status === 400 ? ((await response.json().catch(() => ({}))) as { error_code?: string }).error_code : undefined;
        if (code === 'same_password') return 'SAME';
        if (code === 'weak_password') return 'WEAK';
        throw new Error(`Supabase Auth: HTTP ${response.status}${code ? ` ${code}` : ''}`);
      },
    },
    store,
  );
}
